import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { Cloud, Star, Heart, Sparkles } from "lucide-react";

export function Butterfly({ className = "", size = 34, color = "#F25F9E", style }) {
  return (
    <svg className={className} style={style} width={size} height={size} viewBox="0 0 64 64" fill="none" aria-hidden>
      <g stroke={color} strokeWidth="2">
        <path d="M32 20c-6-12-26-12-26 4 0 12 18 16 26 8" fill={color} fillOpacity="0.85" />
        <path d="M32 20c6-12 26-12 26 4 0 12-18 16-26 8" fill={color} fillOpacity="0.85" />
        <path d="M32 44c-5-6-20-4-24 6 0 8 16 10 24 2" fill={color} fillOpacity="0.6" />
        <path d="M32 44c5-6 20-4 24 6 0 8-16 10-24 2" fill={color} fillOpacity="0.6" />
        <line x1="32" y1="18" x2="32" y2="48" stroke="#4A3B52" strokeWidth="3" strokeLinecap="round" />
      </g>
      <circle cx="28" cy="14" r="2" fill="#4A3B52" />
      <circle cx="36" cy="14" r="2" fill="#4A3B52" />
    </svg>
  );
}

export function Bird({ className = "", size = 26, color = "#7A3CB3", style }) {
  return (
    <svg className={className} style={style} width={size} height={size} viewBox="0 0 64 32" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" aria-hidden>
      <path d="M4 24 C16 6 26 6 32 18 C38 6 48 6 60 24" />
    </svg>
  );
}

// A gentle, slow rainbow shimmer arc high in the sky (very subtle).
function RainbowShimmer() {
  return (
    <motion.svg className="absolute left-1/2 -translate-x-1/2 top-[6%]" width="520" height="180" viewBox="0 0 520 180" aria-hidden
      initial={{ opacity: 0 }} animate={{ opacity: [0, 0.22, 0.16, 0.24, 0] }}
      transition={{ duration: 16, repeat: Infinity, repeatDelay: 6, ease: "easeInOut" }}>
      {["#FF8FB1", "#FFC839", "#8FE39A", "#8FC7FF", "#C79BF0"].map((c, i) => (
        <path key={c} d={`M40 ${170 - i * 12} A ${220 - i * 12} ${150 - i * 12} 0 0 1 ${480 + i * 0} ${170 - i * 12}`}
          fill="none" stroke={c} strokeWidth="7" strokeLinecap="round" />
      ))}
    </motion.svg>
  );
}

// A softly swaying little flower (rooted at its base).
function Flower({ className = "", color = "#F25F9E", size = 30, style }) {
  return (
    <motion.svg className={className} style={{ transformOrigin: "50% 100%", ...style }} width={size} height={size * 1.6} viewBox="0 0 30 48" aria-hidden
      animate={{ rotate: [0, 3.5, -3, 0] }} transition={{ duration: 4 + Math.random() * 2, repeat: Infinity, ease: "easeInOut" }}>
      <line x1="15" y1="20" x2="15" y2="48" stroke="#5DA271" strokeWidth="3" strokeLinecap="round" />
      <path d="M11 30 C4 28 4 34 12 34" fill="#7BC48F" />
      {[0, 72, 144, 216, 288].map((a) => (
        <ellipse key={a} cx="15" cy="7" rx="4.5" ry="7" fill={color} transform={`rotate(${a} 15 15)`} />
      ))}
      <circle cx="15" cy="15" r="4.5" fill="#FFC839" />
    </motion.svg>
  );
}

// Rich but naturally-staggered ambient decorations: drifting clouds, butterflies,
// birds, sparkles, hearts, a faint rainbow shimmer & a few swaying flowers.
export function Decorations({ dense = false }) {
  // Randomize timings ONCE per mount so nothing moves in lockstep (feels alive, not busy).
  const r = useMemo(() => {
    const rnd = (min, max) => min + Math.random() * (max - min);
    const mk = (n, dMin, dMax, durMin, durMax) => Array.from({ length: n }, () => ({
      animationDelay: `${-rnd(0, dMax)}s`, animationDuration: `${rnd(durMin, durMax)}s`,
    }));
    return {
      clouds: mk(3, 0, 40, 44, 66), flutters: mk(3, 0, 8, 6, 11),
      birds: mk(2, 0, 6, 16, 26), twinkles: mk(2, 0, 3, 2.4, 4.2),
      floaties: mk(4, 0, 4, 4, 7),
    };
  }, []);

  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      {/* soft color blobs */}
      <div className="absolute -left-16 top-24 h-56 w-56 rounded-full bg-goldK/20 blur-3xl" />
      <div className="absolute right-0 top-1/3 h-64 w-64 rounded-full bg-tealK/20 blur-3xl" />
      <div className="absolute left-1/3 bottom-0 h-52 w-52 rounded-full bg-purpleK/15 blur-3xl" />

      <RainbowShimmer />

      {/* drifting clouds (staggered) */}
      <Cloud className="absolute left-[-6%] top-[12%] text-white/90 animate-driftFar" style={r.clouds[0]} size={90} fill="white" strokeWidth={1.5} />
      <Cloud className="absolute left-[-6%] top-[30%] text-white/80 animate-driftFar" style={r.clouds[1]} size={64} fill="white" strokeWidth={1.5} />
      {dense && <Cloud className="absolute left-[-6%] top-[52%] text-white/70 animate-driftFar" style={r.clouds[2]} size={54} fill="white" strokeWidth={1.5} />}

      {/* butterflies (staggered) */}
      <Butterfly className="absolute left-[14%] top-[40%] animate-flutter" style={r.flutters[0]} color="#F25F9E" size={38} />
      <Butterfly className="absolute right-[18%] top-[26%] animate-flutter" style={r.flutters[1]} color="#9B51E0" size={30} />
      {dense && <Butterfly className="absolute right-[30%] bottom-[18%] animate-flutter" style={r.flutters[2]} color="#26C0B4" size={34} />}

      {/* birds (staggered) */}
      <Bird className="absolute right-[10%] top-[14%] animate-drift" style={r.birds[0]} color="#7A3CB3" size={30} />
      <Bird className="absolute right-[16%] top-[18%] animate-drift" style={r.birds[1]} color="#7A3CB3" size={22} />

      {/* sparkles + hearts + stars (staggered) */}
      <Sparkles className="absolute left-[24%] top-[16%] text-goldK animate-twinkle" style={r.twinkles[0]} size={28} />
      <Sparkles className="absolute right-[26%] top-[58%] text-purpleK animate-twinkle" style={r.twinkles[1]} size={24} />
      <Star className="absolute left-[8%] top-[64%] text-goldK animate-floaty" style={r.floaties[0]} size={26} fill="#FFC839" />
      <Heart className="absolute right-[8%] bottom-[16%] text-pinkK animate-floatySlow" style={r.floaties[1]} size={30} fill="#F25F9E" />
      <Heart className="absolute left-[46%] top-[8%] text-purpleK animate-floaty" style={r.floaties[2]} size={22} fill="#9B51E0" />

      {/* a few gently-swaying flowers rooted along the bottom */}
      <Flower className="absolute left-[6%] bottom-0 opacity-80" color="#F25F9E" size={26} />
      <Flower className="absolute left-[15%] bottom-0 opacity-70" color="#9B51E0" size={22} style={{ animationDelay: "-1.5s" }} />
      {dense && <Flower className="absolute right-[7%] bottom-0 opacity-80" color="#FFC839" size={28} style={{ animationDelay: "-2.2s" }} />}
      {dense && <Flower className="absolute right-[16%] bottom-0 opacity-70" color="#26C0B4" size={22} style={{ animationDelay: "-0.8s" }} />}
    </div>
  );
}
